import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/shared/config/env';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
} from '@/shared/lib/storage/token';
import { endpoints } from '@/shared/api/endpoints';
import { ApiError } from '@/shared/api/errors';
import { clearClientSession } from '@/shared/lib/auth/clearClientSession';

export type HttpClientConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  /** When true, a 401 will not clear session or redirect to login. */
  skipAuthLogout?: boolean;
};

export const httpClient = axios.create({
  baseURL: env.API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'x-platform': 'ADVERTISER',
  },
});

function resolveCountry(): string {
  return env.COUNTRY;
}

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['x-country'] = resolveCountry();
  config.headers['x-timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return config;
});

let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function flushQueue(error: unknown, token: string | null) {
  pendingQueue.forEach((item) => {
    if (error) item.reject(error);
    else if (token) item.resolve(token);
  });
  pendingQueue = [];
}

function isSignupOnboardingPath(pathname: string) {
  return (
    pathname === '/signup' ||
    pathname === '/otp-verify' ||
    pathname === '/user-detail' ||
    pathname === '/create-password' ||
    pathname === '/forgot-password' ||
    pathname === '/change-password'
  );
}

function logoutAndRedirect() {
  // Never wipe signup SESSION_ID / bounce to login during onboarding.
  if (isSignupOnboardingPath(window.location.pathname)) {
    return;
  }

  clearClientSession();
  const publicPaths = [
    '/',
    '/login',
    '/signin',
    '/signup',
    '/otp-verify',
    '/forgot-password',
    '/change-password',
    '/tutorial',
    '/partner/kyc-registration',
  ];
  const isPublic =
    publicPaths.includes(window.location.pathname) ||
    window.location.pathname.startsWith('/billboard') ||
    window.location.pathname.startsWith('/partner/') ||
    window.location.pathname === '/billboards';
  if (!isPublic) {
    window.location.assign('/login');
  }
}

function toApiError(error: AxiosError<{ message?: string; code?: string }>) {
  return new ApiError(
    error.response?.data?.message || error.message || 'Request failed',
    error.response?.status ?? 0,
    error.response?.data?.code,
    error.response?.data,
  );
}

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ message?: string; code?: string }>) => {
    const original = error.config as HttpClientConfig | undefined;

    if (error.response?.status !== 401 || !original || original._retry) {
      throw toApiError(error);
    }

    // Signup file delete (and similar) can 401 with only SESSION_ID — don't log the user out.
    if (original.skipAuthLogout) {
      throw toApiError(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token: string) => {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(httpClient(original));
          },
          reject,
        });
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token');
      }

      const { data } = await axios.post(
        `${env.API_BASE_URL}${endpoints.auth.refresh}`,
        { refreshToken },
        { withCredentials: true },
      );

      const accessToken = data?.accessToken ?? data?.data?.accessToken;
      const nextRefresh = data?.refreshToken ?? data?.data?.refreshToken;
      if (!accessToken) throw new Error('Refresh failed');

      setTokens(accessToken, nextRefresh);
      flushQueue(null, accessToken);
      original.headers.Authorization = `Bearer ${accessToken}`;
      return httpClient(original);
    } catch (refreshError) {
      flushQueue(refreshError, null);
      logoutAndRedirect();
      throw refreshError;
    } finally {
      isRefreshing = false;
    }
  },
);
